import React, { useEffect, useMemo, useState } from "react";
import { DatePicker } from "@arco-design/web-react";
import { IconInfoCircle } from "@arco-design/web-react/icon";
import DataTable from "../../Components/DataTable/DataTable";
import Loader from "../../Components/LoaderComponent/Loader";
import { useHeadlessAppHitLogs } from "./useHeadlessAppHitLogs";
import { formatDateTime } from "../../Functions/Utils";
import {
  HEADLESS_APP_LABELS,
  HEADLESS_APP_COLUMNS,
  DEFAULT_FILTERS,
  DEFAULT_PAGINATION,
} from "./headlessAppHitLogsConfig";
import { useStoreValues } from "../../Context/storeProvider";

function HeadlessAppHitLogs() {
  const {
    deviceList,
    accountList,
    sourceList,
    docTypeList,
    statusList,
    tableData,
    total,
    isLoading,
    fetchSummary,
    downloadSummary,
  } = useHeadlessAppHitLogs();

  const { user_Roll } = useStoreValues();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const columns = useMemo(() => HEADLESS_APP_COLUMNS(formatDateTime), []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateChange = (range) => {
    handleFilterChange("startDate", range?.[0] || null);
    handleFilterChange("endDate", range?.[1] || null);
  };

  const handleApplyFilters = () => {
    setPagination({ ...pagination, current: 1 });
    fetchSummary(filters, { ...pagination, current: 1 });
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPagination(DEFAULT_PAGINATION);
    fetchSummary(DEFAULT_FILTERS, DEFAULT_PAGINATION);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    fetchSummary(filters, newPagination);
  };

  useEffect(() => {
    if (deviceList?.length) {
      fetchSummary(filters, pagination);
    }
  }, [deviceList, accountList]);

  return (
    <div className="admin_Section overflow-auto scrollbar1 px-3 headless_app_wrapper">
      {isLoading && <Loader />}

      <div className="d-flex justify-content-between align-items-center mb-2 py-2">
        <div>
          <h3 className="font_18 fw_600 mb-3 mb-lg-0">
            {HEADLESS_APP_LABELS.title}
          </h3>
        </div>
        <button
          className="btn px_btn btn-master-line px-3 py-2 rounded font_12 fw_500"
          disabled={!tableData?.length}
          onClick={() => downloadSummary(filters)}
        >
          {HEADLESS_APP_LABELS.download}
        </button>
      </div>

      {/* Filters */}
      <div className="row align-items-center mb-3">
        <div className="col-12 col-lg-12">
          <div className="align-items-center d-flex flex-column flex-md-row gap-2">
            {/* Device List */}
            <div className="d-flex flex-column w-100">
              <span className="font_14 fw_500 ws_nowrp">
                {HEADLESS_APP_LABELS.deviceList}
              </span>
              <select
                className="form-control"
                value={filters.deviceId}
                onChange={(e) => handleFilterChange("deviceId", e.target.value)}
              >
                <option value="">{HEADLESS_APP_LABELS.selectDevice}</option>
                {deviceList.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.value}
                  </option>
                ))}
              </select>
            </div>
            {/* Account List */}
            {user_Roll == "Admin" && (
              <div className="d-flex flex-column w-100">
                <span className="font_14 fw_500 ws_nowrp">
                  {HEADLESS_APP_LABELS.accountList}
                </span>
                <select
                  className="form-control"
                  value={filters.clientId}
                  onChange={(e) =>
                    handleFilterChange("clientId", e.target.value)
                  }
                >
                  <option key="" value="">
                    {HEADLESS_APP_LABELS.selectAccount}
                  </option>
                  {accountList.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.value}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Source Filter */}
            <div className="d-flex flex-column w-100">
              <span className="font_14 fw_500 ws_nowrp">
                {HEADLESS_APP_LABELS.source}
              </span>
              <select
                className="form-control"
                value={filters.source}
                onChange={(e) => handleFilterChange("source", e.target.value)}
              >
                <option value="">{HEADLESS_APP_LABELS.selectSource}</option>
                {sourceList.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.value}
                  </option>
                ))}
              </select>
            </div>
            {/* Doc Type Filter */}
            <div className="d-flex flex-column w-100">
              <span className="font_14 fw_500 ws_nowrp">
                {HEADLESS_APP_LABELS.docType}
              </span>
              <select
                className="form-control"
                value={filters.docType}
                onChange={(e) => handleFilterChange("docType", e.target.value)}
              >
                <option value="">{HEADLESS_APP_LABELS.selectDocType}</option>
                {docTypeList.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.value}
                  </option>
                ))}
              </select>
            </div>
            {/* Status Filter */}
            <div className="d-flex flex-column w-100">
              <span className="font_14 fw_500 ws_nowrp">
                {HEADLESS_APP_LABELS.status}
              </span>
              <select
                className="form-control"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">{HEADLESS_APP_LABELS.selectStatus}</option>
                {statusList.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.value}
                  </option>
                ))}
              </select>
            </div>
            {/* Date Range */}
            <div className="d-flex flex-column w-100">
              <span className="font_14 fw_500 ws_nowrp">
                {HEADLESS_APP_LABELS.date}
              </span>
              <DatePicker.RangePicker
                value={[filters.startDate, filters.endDate]}
                onChange={handleDateChange}
                allowClear
                style={{ minWidth: 260 }}
                disabledDate={(current) => current.isAfter(Date.now())}
                prefix={<IconInfoCircle />}
              />
            </div>

            {/* Actions */}
            <div className="d-flex flex-column w-100 flex-md-row mt-1 pt-3">
              <button
                className="btn px_btn btn-master px-4 py-2 rounded fw_500 font_12 w-100"
                onClick={handleApplyFilters}
              >
                {HEADLESS_APP_LABELS.filter}
              </button>
              <button
                className="mt-2 ms-0 ms-md-2 mt-md-0 btn px_btn btn-master-line px-3 py-2 rounded fw_500 font_12 w-100"
                onClick={handleReset}
              >
                {HEADLESS_APP_LABELS.reset}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columnData={columns}
        rowData={tableData}
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={total}
        loading={isLoading}
        onChangeTable={handleTableChange}
      />
    </div>
  );
}

export default HeadlessAppHitLogs;
